/** @format */

import {EyeOffIcon} from '@heroicons/react/outline'
import {ChevronRightIcon, LockClosedIcon} from '@heroicons/react/solid'
import type {Row} from '@tanstack/react-table'
import clsx from 'clsx'
import React, {type PropsWithChildren} from 'react'

import {Checkbox} from '@src/components/tailwind/CheckboxV2'
import {useMainElement} from '@src/components/tailwind/DashboardLayout'
import {StatusIndicator} from '@src/components/tailwind/StatusIndicator'
import {UpgradeLink} from '@src/components/tailwind/UpgradeLink'

import {useAccountContext, useFormApi} from '@src/contexts'

import {RESERVED_FIELDS} from './constants'
import {DetailPanel} from './DetailPanel'
import {EmptyTable} from './EmptyTable'
import {useSubmissionsContext, type Submission} from './SubmissionsContext'
import {EmptyTableCell, TableCell} from './TableCell'
import {DragAndDropTableHeaderProvider, TableHeader} from './TableHeader'
import {useTableModel} from './TableModel'
import {
  formatDate,
  getColumnSizeVar,
  getStatusIndicatorType,
  sanitizeFieldName,
} from './utils'

// Tooltip on hover over a submission past the archive limit, which explains why
// the row doesn't open when clicked.
const PAST_LIMIT_ROW_TITLE =
  "This submission is older than your plan's archive limit. Upgrade to view it."

// Tooltip on hover over an over-limit submission, which the API sends with
// every column cut short and which selects rather than opens when clicked.
const OVER_LIMIT_ROW_TITLE =
  "This submission wasn't processed because your account was over its monthly limit. Select it and reprocess it to view it."

// Read out in place of the tooltip, which screen readers and touch skip.
const OVER_LIMIT_ROW_LABEL =
  'Preview only. Reprocess this submission to read it.'

// How many past-retention submissions to preview before the upgrade prompt.
// The API sends this many — see PAST_RETENTION_PREVIEW_LIMIT in
// formspree/models/form.py. Any beyond it are dropped, not rendered.
const MAX_PAST_LIMIT_ROWS = 2

// Rows the table pads out to, so a short page keeps its full height.
const MIN_TABLE_ROWS = 10

export function Table() {
  const main = useMainElement()
  const {data, pagination} = useSubmissionsContext()
  const {columnVisibility, table} = useTableModel()

  // To avoid the performance cost of calling `column.getSize()` on every render
  // for each header and data cell, we will calculate all column sizes once
  // at the root table level using `useMemo`. We will then pass these sizes
  // as CSS variables to the root element of the table.
  const {columnSizing} = table.getState()
  const columnSizeVars = React.useMemo(() => {
    const colSizes: Record<string, string> = {}
    for (const h of table.getFlatHeaders()) {
      if (h.column.getCanResize()) {
        colSizes[getColumnSizeVar(h.id)] = convertToRem(h.getSize())
      }
    }
    return colSizes
    // Explicitly define dependencies here to trigger re-renders correctly.
    // Since the `table` instance itself is stable, passing it alone won't work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSizing, columnVisibility])

  const {rows} = table.getRowModel()

  if (rows.length === 0) {
    return <EmptyTable columnSizeVars={columnSizeVars} />
  }

  // Past-limit rows can't be selected or opened, so they're previewed under
  // the prompt rather than listed. The API resends the same ones on every
  // response that runs out of readable rows, so we render them on the last
  // page only.
  const isLastPage = pagination.pageIndex === data.total_pages - 1
  const accessibleRows = rows.filter(row => row.getCanSelect())
  const pastLimitRows = isLastPage
    ? rows.filter(row => !row.getCanSelect()).slice(0, MAX_PAST_LIMIT_ROWS)
    : []

  // Short pages are padded so the table doesn't collapse, but the past-limit
  // block already fills that space.
  const fillerRows =
    pastLimitRows.length > 0
      ? 0
      : Math.max(0, MIN_TABLE_ROWS - accessibleRows.length)

  return (
    <div className="max-w-full overflow-hidden">
      {/* Mobile card layout */}
      <div className="sm:hidden">
        <div className="divide-y divide-gray-100">
          {accessibleRows.map(row => (
            <MobileSubmissionCard key={row.id} row={row} />
          ))}
        </div>
        {pastLimitRows.length > 0 && (
          <PastLimitSection>
            <div className="divide-y divide-gray-200">
              {pastLimitRows.map(row => (
                <MobileSubmissionCard key={row.id} row={row} />
              ))}
            </div>
          </PastLimitSection>
        )}
      </div>

      {/* Desktop table layout */}
      <div className="hidden overflow-x-auto sm:block" style={columnSizeVars}>
        <DragAndDropTableHeaderProvider>
          <div className="flex">
            {table.getHeaderGroups()[0].headers.map(header => (
              <TableHeader header={header} key={header.id} />
            ))}
          </div>
        </DragAndDropTableHeaderProvider>

        {accessibleRows.map(row => (
          <TableRow key={row.id} row={row} />
        ))}

        {pastLimitRows.length > 0 && (
          <PastLimitSection indent>
            {pastLimitRows.map(row => (
              <TableRow key={row.id} row={row} />
            ))}
          </PastLimitSection>
        )}

        {/* Fill the table with empty rows if the number of rows is too small */}
        {Array.from({length: fillerRows}, (_, i) => (
          <div className="flex" key={i}>
            {table.getVisibleLeafColumns().map(c => (
              <EmptyTableCell column={c} key={c.id} />
            ))}
          </div>
        ))}
      </div>

      <DetailPanel container={main} />
    </div>
  )
}

type PastLimitSectionProps = PropsWithChildren<{
  // Desktop rows lead with the selection and status columns; indent the prompt
  // by the same amount so it lines up with the submission text above it.
  indent?: boolean
}>

// Everything past the retention limit, set apart from the accessible rows by a
// dashed rule and a grey field the previews fade down into.
function PastLimitSection(props: PastLimitSectionProps) {
  const {children, indent} = props
  return (
    <div className="border-t border-dashed border-gray-300 bg-gray-100">
      <div className="relative">
        {children}
        {/* carries the row fade smoothly into the panel; above z-[1] so it
            covers the sticky selection column too */}
        <div className="pointer-events-none absolute inset-0 z-[2] bg-gradient-to-b from-gray-100/60 via-gray-100/85 to-gray-100" />
      </div>

      <div className="flex">
        {indent && (
          <>
            <div className="flex-none p-1.5">
              <div className="h-4 w-4" />
            </div>
            <div className="w-8 flex-none" />
          </>
        )}
        <PastLimitPrompt />
      </div>
    </div>
  )
}

function PastLimitPrompt() {
  const {account} = useAccountContext()

  return (
    <div className="grow px-4 pb-8">
      <div className="mt-4 max-w-xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-50">
          <LockClosedIcon className="h-[18px] w-[18px] text-primary" />
        </span>

        <p className="mt-4 text-lg font-bold text-gray-900">
          Your plan only archives submissions for{' '}
          {account.limits.retention.limit} days!
        </p>
        <p className="mt-3 text-base leading-relaxed text-gray-600">
          To view, search, and export your older submissions, you&rsquo;ll need
          any of our paid plans &mdash; all of which include unlimited archive
          history.
        </p>

        <UpgradeLink className="buttonPrimary mt-5" feature="retention">
          Unlock unlimited history
        </UpgradeLink>
      </div>
    </div>
  )
}

type TableRowProps = {
  row: Row<Submission>
}

function TableRow(props: TableRowProps) {
  const {row} = props
  const {table} = useTableModel()

  if (!row.getCanSelect()) {
    return (
      <div className="flex" key={row.id} title={PAST_LIMIT_ROW_TITLE}>
        {row.getVisibleCells().map(cell => (
          <TableCell cell={cell} key={cell.id} />
        ))}
      </div>
    )
  }

  if (!row.getCanExpand()) {
    // Over-limit: the API cut every column short, so there is nothing to
    // open; clicking selects the row instead, ready for Reprocess. The first
    // content cell carries the marker. `canExpand` here only keeps the cells
    // on the white row background rather than the past-limit grey.
    const cells = row.getVisibleCells()
    const markedCell = cells.find(cell => !RESERVED_FIELDS.has(cell.column.id))
    return (
      <div
        className="group flex cursor-pointer"
        key={row.id}
        onClick={() => row.toggleSelected()}
        title={OVER_LIMIT_ROW_TITLE}
      >
        <span className="sr-only">{OVER_LIMIT_ROW_LABEL}</span>
        {cells.map(cell => (
          <TableCell
            canExpand
            cell={cell}
            key={cell.id}
            redacted={cell === markedCell ? 'marked' : true}
          />
        ))}
      </div>
    )
  }

  return (
    <div
      data-state={row.getIsExpanded() ? 'expanded' : 'collapsed'}
      className="group flex cursor-pointer"
      key={row.id}
      onClick={() =>
        // only 1 detail panel open at a time
        row.getIsExpanded()
          ? table.resetExpanded()
          : table.setExpanded({[row.id]: true})
      }
    >
      {row.getVisibleCells().map(cell => (
        <TableCell canExpand cell={cell} key={cell.id} />
      ))}
    </div>
  )
}

function MobileSubmissionCard({row}: {row: Row<Submission>}) {
  const {table} = useTableModel()
  const {current: currentForm} = useFormApi()
  const canExpand = row.getCanExpand()
  // selectable but not openable: the API cut every column short
  const isOverlimit = !canExpand && row.getCanSelect()
  const statusType = getStatusIndicatorType(row.original._status)
  const previewEntries = Object.entries(row.original)
    .filter(([key]) => !RESERVED_FIELDS.has(key))
    .slice(0, 2)

  return (
    <div
      className={clsx(
        'flex items-start gap-x-3 px-4 py-3',
        canExpand &&
          (row.getIsExpanded()
            ? 'bg-gray-100'
            : 'cursor-pointer hover:bg-gray-50 active:bg-gray-100'),
        isOverlimit && 'cursor-pointer',
      )}
      title={
        canExpand
          ? undefined
          : isOverlimit
            ? OVER_LIMIT_ROW_TITLE
            : PAST_LIMIT_ROW_TITLE
      }
      onClick={
        canExpand
          ? () =>
              row.getIsExpanded()
                ? table.resetExpanded()
                : table.setExpanded({[row.id]: true})
          : isOverlimit
            ? () => row.toggleSelected()
            : undefined
      }
    >
      {isOverlimit && <span className="sr-only">{OVER_LIMIT_ROW_LABEL}</span>}
      <div className="mt-0.5 shrink-0" onClick={e => e.stopPropagation()}>
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={() => row.toggleSelected()}
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-x-1.5">
          {statusType && (
            <StatusIndicator className="h-4 w-4 shrink-0" type={statusType} />
          )}
          <time className="text-xs text-gray-500" dateTime={row.original._date}>
            {formatDate(row.original._date, currentForm.timezone)}
          </time>
          {isOverlimit && (
            <EyeOffIcon
              aria-hidden
              className="h-4 w-4 shrink-0 text-gray-400"
            />
          )}
        </div>
        {previewEntries.map(([field, value]) => (
          <p
            key={field}
            className={clsx(
              'mt-0.5 truncate text-sm',
              isOverlimit ? 'text-gray-500' : 'text-gray-700',
            )}
          >
            <span className="font-medium">{sanitizeFieldName(field)}: </span>
            {typeof value === 'string' ? value : JSON.stringify(value)}
          </p>
        ))}
      </div>

      {canExpand && (
        <ChevronRightIcon
          className={clsx(
            'mt-0.5 h-5 w-5 shrink-0 text-gray-400 transition-transform',
            row.getIsExpanded() && 'rotate-90',
          )}
        />
      )}
    </div>
  )
}

function convertToRem(pixels: number): string {
  return pixels / 16 + 'rem'
}
